import { FC, useState, useMemo } from 'react'
import {
  Box,
  Button,
  Stack,
  Slider,
  SliderMark,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Text,
  Heading,
  useToast,
  VStack,
  HStack,
  Tag
} from '@chakra-ui/react'
import _ from 'lodash'

import { openPaymentPage } from 'controllers/stripe'
import { useSelector } from 'model/hooks'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import DateTypeSelect from 'components/stripe/DateTypeSelect'
import PlanFeatures from 'components/stripe/planSelect/PlanFeatures'
import { getPricesByInterval } from 'model/selectors/paymentPlans'
import numeral from 'numeral'
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons'

const PlanSelect: FC = () => {
  const [isYearType, setIsYearType] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [index, setIndex] = useState<number>(0)
  const products = useSelector(state => state.products)
  const pricesByInerval = useSelector(getPricesByInterval)
  const toast = useToast()

  const pricesList = useMemo(
    () => (isYearType ? pricesByInerval.year : pricesByInerval.month),
    [isYearType, pricesByInerval]
  )

  const customPrice = useMemo(() => {
    return _.last(pricesList)
  }, [pricesList])

  const customProduct = useMemo(() => {
    if (customPrice && products) {
      return products[customPrice.product]
    }
  }, [customPrice, products])

  const currentPrice = useMemo(() => {
    return _.has(pricesList, index) ? pricesList[index] : customPrice
  }, [index, pricesList, customPrice])

  const currentProduct = useMemo(() => {
    return products && currentPrice ? products[currentPrice.product] : null
  }, [currentPrice, products])

  const isCustomPlan = useMemo(() => {
    return !_.has(pricesList, index)
  }, [pricesList, index])

  const monthlyPrice = useMemo(() => {
    if (currentPrice) {
      if (isYearType) {
        return currentPrice.unit_amount / 12
      } else {
        return currentPrice.unit_amount
      }
    } else {
      return 0
    }
  }, [isYearType, currentPrice])

  const onSubmit = async () => {
    if (currentPrice && currentProduct) {
      if (isCustomPlan) {
        window.open('mailto:sales@facefile.co?subject=Subscription', '_blank')
      } else {
        setLoading(true)
        const res = await openPaymentPage(currentPrice.id)
        if (!res) {
          toast({
            title: 'Upgrade plan',
            description: 'Error occured',
            status: 'error',
            duration: 9000,
            isClosable: true
          })
        }
        setLoading(false)
      }
    }
  }

  if (_.isEmpty(pricesList) || !currentProduct || !currentPrice) {
    return null
  }

  const renderSlider = () => {
    return (
      <Box w='full' flexShrink={0} h={'14'} pt={2} px={3}>
        <Slider
          value={index}
          onChange={(val: number) => setIndex(val)}
          min={0}
          max={_.size(pricesList)}
          colorScheme='teal'
        >
          <SliderTrack h={1.5} bg={'gray.200'}>
            <SliderFilledTrack />
          </SliderTrack>
          <SliderThumb w={5} h={5} borderWidth={2} borderColor={'teal.400'} />
          {_.map(pricesList, (price, i: number) => {
            const p = _.get(products, price.product)
            return (
              <SliderMark
                value={i}
                fontSize='xs'
                key={i}
                pt={6}
                position='relative'
              >
                <Text
                  fontWeight={'semibold'}
                  color={index >= i ? 'gray.800' : 'gray.500'}
                  textAlign='center'
                  pointerEvents={'all'}
                  transform={'translate(-50%, 0%)'}
                  onClick={() => setIndex(i)}
                >
                  {p?.metadata.amount}
                </Text>
              </SliderMark>
            )
          })}
          <SliderMark
            value={_.size(pricesList)}
            fontSize='xs'
            pt={6}
            position='relative'
          >
            <Text
              fontWeight={'semibold'}
              color={index >= _.size(pricesList) ? 'gray.800' : 'gray.500'}
              textAlign='center'
              pointerEvents={'all'}
              transform={'translate(-50%, 0%)'}
              onClick={() => setIndex(_.size(pricesList))}
            >
              {customProduct?.metadata.amount}+
            </Text>
          </SliderMark>
        </Slider>
      </Box>
    )
  }

  const renderCurrentPlanPrice = () => {
    if (currentPrice && currentProduct && isCustomPlan) {
      return (
        <Heading size='xl' color='teal.500'>
          Custom pricing
        </Heading>
      )
    } else if (currentPrice && currentProduct) {
      const strPrice = numeral(monthlyPrice / 100).format('$0')
      return (
        <Heading size='2xl' color='teal.500'>
          {strPrice}/mo
        </Heading>
      )
    }
  }

  const renderSelectedPlanInfo = () => {
    return (
      <VStack align='flex-start' w='full' spacing={6}>
        <HStack w='full' justify={'space-between'}>
          <Text color='gray.800' fontSize='3xl' fontWeight={'bold'}>
            Pro
          </Text>
          {currentPrice.metadata.mostPopular === 'true' && (
            <Tag
              colorScheme='blue'
              variant='solid'
              rounded={'base'}
              px={4}
              fontWeight='bold'
            >
              Most Popular
            </Tag>
          )}
        </HStack>
        <Box h={12} flexShrink={0}>
          {renderCurrentPlanPrice()}
        </Box>
      </VStack>
    )
  }

  return (
    <VStack spacing={10} w='full'>
      <DateTypeSelect
        isYearType={isYearType}
        onChange={(v: boolean) => setIsYearType(v)}
      />
      <Stack
        w='full'
        justify='space-between'
        align='center'
        spacing='30'
        direction={{ base: 'column', lg: 'row' }}
      >
        <VStack
          w='full'
          p={{ base: '0', lg: '10' }}
          align='flex-start'
          spacing={6}
        >
          {renderSelectedPlanInfo()}
          {renderSlider()}
          <HStack w='full' justify={'center'} pb={2}>
            <Text textAlign='center' fontWeight={'semibold'} color='teal.400'>
              {`${currentProduct.metadata.amount}${
                isCustomPlan ? '+' : ''
              } responses per month`}
            </Text>
          </HStack>
          <Button
            onClick={onSubmit}
            w='full'
            rightIcon={<FontAwesomeIcon icon={faArrowRight} />}
            colorScheme='teal'
            isLoading={loading}
          >
            {isCustomPlan ? 'Contact sales' : 'Upgrade Now'}
          </Button>
        </VStack>
        <PlanFeatures
          price={numeral(monthlyPrice / 100).format('$0')}
          isCustom={isCustomPlan}
          isMonth
        />
      </Stack>
    </VStack>
  )
}

export default PlanSelect
